import React from 'react';
import { Icon } from '../components/Core/Icon';
import { FileMenuActionEnum } from '../enums/FileMenuActionEnum';
import { FilesTableEnum } from '../enums/FilesTableEnum';
import File from '../interfaces/File';
import Tag from '../interfaces/Tag';
import User from '../interfaces/User';
import { translate } from '../utils/translation';
import { isFileSpecialFolder } from '../utils/utils';
import FileAction from 'interfaces/FileAction';
import FileActionContext from 'interfaces/FileActionContext';

export type FileActionKey = FileMenuActionEnum | `tag-${number}`;

export const fileActions: FileAction[] = [
  {
    key: FileMenuActionEnum.Open,
    label: translate('common.open'),
    icon: <Icon name="SquareArrowOutUpRight" className="h-5 w-5" />,
    condition: (context) => {
      return (
        context.files.length === 1 &&
        !context.isInFileRoute(FilesTableEnum.Trash) &&
        !context.files.some((f) => isFileSpecialFolder(f)) &&
        !context.files.some((f) => f.isQuickShare)
      );
    },
  },
  {
    key: FileMenuActionEnum.OpenShortcutOriginalFileLocation,
    label: translate('common.openFileLocation'),
    icon: <Icon name="FolderSymlinkIcon" className="h-5 w-5" />,
    condition: (context) => {
      return (
        context.files.length === 1 &&
        context.files?.[0]?.isShortcut &&
        !context.isInFileRoute(FilesTableEnum.Trash) &&
        !context.files.some((f) => isFileSpecialFolder(f)) &&
        !context.files.some((f) => f.isQuickShare)
      );
    },
  },
  {
    key: FileMenuActionEnum.Divider,
    type: 'divider',
  },
  {
    key: FileMenuActionEnum.Download,
    label: translate('common.download'),
    icon: <Icon name="CloudDownloadIcon" className="h-5 w-5" />,
    condition: (context) => {
      return (
        !context.files.some((f) => f?.readOnly) &&
        !context.isInFileRoute(FilesTableEnum.Trash) &&
        context.isDownloadAllowedForFiles(context.files) &&
        !context.files.some((f) => isFileSpecialFolder(f))
      );
    },
  },
  {
    key: FileMenuActionEnum.DownloadEncrypted,
    label: translate('common.downloadEncrypted'),
    icon: <Icon name="ShieldCheckIcon" className="h-5 w-5" />,
    condition: (context) => {
      return (
        context.files.length === 1 &&
        !context.files.some((f) => f?.isFolder) &&
        !context.isInFileRoute(FilesTableEnum.Trash) &&
        !context.files.some((f) => isFileSpecialFolder(f))
      );
    },
  },
  {
    key: FileMenuActionEnum.Divider,
    type: 'divider',
  },
  {
    key: FileMenuActionEnum.Share,
    label: translate('common.share'),
    icon: <Icon name="FileUserIcon" className="h-5 w-5" />,
    condition: (context) => {
      return (
        !context.isInFileRoute(FilesTableEnum.Trash) &&
        !context.isInFileRoute(FilesTableEnum.Hidden) &&
        context.isAdminOrContributor &&
        context.files.every(
          (f) => f?.ownerId === context.authenticatedUser?.userId
        ) &&
        !context.files.some((f) => isFileSpecialFolder(f)) &&
        !context.files.some((f) => f.isQuickShare)
      );
    },
  },

  {
    key: FileMenuActionEnum.SellFile,
    label: translate('common.sell'),
    icon: <Icon name="StoreIcon" className="h-5 w-5" />,
    condition: (context) => {
      return (
        !context.isInFileRoute(FilesTableEnum.Trash) &&
        !context.isInFileRoute(FilesTableEnum.Hidden) &&
        !context.isInFileRoute(FilesTableEnum.Shared) &&
        context.isAdminOrContributor &&
        context.files.every(
          (f) => f?.ownerId === context.authenticatedUser?.userId
        ) &&
        !context.files.some((f) => isFileSpecialFolder(f)) &&
        !context.files.some((f) => f.isQuickShare) &&
        import.meta.env.VITE_APP_STORE_ENABLED === 'true'
      );
    },
  },
  {
    key: FileMenuActionEnum.Divider,
    type: 'divider',
  },
  {
    key: FileMenuActionEnum.Rename,
    label: translate('common.rename'),
    icon: <Icon name="FilePenLineIcon" className="h-5 w-5" />,
    condition: (context) => {
      return (
        context.files.length === 1 &&
        !context.isReader &&
        !context.isInFileRoute(FilesTableEnum.Trash) &&
        context.files.every((f) => !f?.readOnly) &&
        !context.files.some((f) => isFileSpecialFolder(f)) &&
        !context.files.some((f) => f.isQuickShare)
      );
    },
  },
  {
    key: FileMenuActionEnum.Organize,
    label: translate('common.organize'),
    type: 'submenu',
    icon: <Icon name="FolderIcon" className="mt-0.5 h-5 w-5" />,
    condition: (context) => true,
    children: [
      {
        key: FileMenuActionEnum.Replace,
        label: translate('common.replace'),
        icon: <Icon name="ReplaceIcon" className="h-5 w-5" />,
        condition: (context) => {
          if (
            !context.isInFileRoute(FilesTableEnum.Shared) &&
            !context.isInFileRoute(FilesTableEnum.Favorites) &&
            !context.isInFileRoute(FilesTableEnum.Hidden) &&
            !context.files.some((f) => isFileSpecialFolder(f)) &&
            !context.files.some((f) => f.isQuickShare)
          ) {
            return (
              context.files.length === 1 &&
              context.isAdminOrContributor &&
              !context.files.some((f) => f.isFolder) &&
              !context.isInFileRoute(FilesTableEnum.Trash) &&
              !context.files.some((f) => isFileSpecialFolder(f)) &&
              !context.files.some((f) => f.isQuickShare) &&
              context.files.every((f) => !f.readOnly)
            );
          } else {
            return (
              context.files.length === 1 &&
              !context.files.some((f) => f.isFolder) &&
              context.isCurrentFolderReadOnly &&
              !context.isInFileRoute(FilesTableEnum.Trash) &&
              !context.files.some((f) => isFileSpecialFolder(f)) &&
              !context.files.some((f) => f.isQuickShare) &&
              !context.isInFileRoute(FilesTableEnum.Received)
            );
          }
        },
      },
      {
        key: FileMenuActionEnum.Duplicate,
        icon: <Icon name="FilesIcon" className="h-5 w-5" />,
        label: translate('common.createCopy'),
        condition: (context) => {
          if (
            !context.isInFileRoute(FilesTableEnum.Shared) &&
            !context.isInFileRoute(FilesTableEnum.Favorites) &&
            !context.isInFileRoute(FilesTableEnum.Hidden)
          ) {
            return (
              !context.files.some((f) => f.isFolder) &&
              context.isAdminOrContributor &&
              !context.isInFileRoute(FilesTableEnum.Trash) &&
              !context.files.some((f) => isFileSpecialFolder(f)) &&
              !context.files.some((f) => f.isQuickShare) &&
              !context.isUploadDisabledInCurrentFolder
            );
          } else {
            return (
              !context.files.some((f) => f.isFolder) &&
              context.isCurrentFolderReadOnly &&
              !context.isInFileRoute(FilesTableEnum.Trash) &&
              !context.files.some((f) => isFileSpecialFolder(f)) &&
              !context.files.some((f) => f.isQuickShare) &&
              !context.isUploadDisabledInCurrentFolder
            );
          }
        },
      },
      {
        key: FileMenuActionEnum.Move,
        icon: <Icon name="FolderInputIcon" className="h-5 w-5" />,
        label: translate('common.move'),
        condition: (context) => {
          if (
            !context.isInFileRoute(FilesTableEnum.Shared) &&
            !context.isInFileRoute(FilesTableEnum.Favorites) &&
            !context.isInFileRoute(FilesTableEnum.Hidden)
          ) {
            return (
              context.isAdminOrContributor &&
              !context.isInFileRoute(FilesTableEnum.Trash) &&
              !context.files.some((f) => isFileSpecialFolder(f)) &&
              !context.files.some((f) => f.isQuickShare) &&
              !context.isUploadDisabledInCurrentFolder
            );
          } else {
            return (
              context.isCurrentFolderReadOnly &&
              !context.isInFileRoute(FilesTableEnum.Trash) &&
              !context.files.some((f) => isFileSpecialFolder(f)) &&
              !context.files.some((f) => f.isQuickShare) &&
              !context.isUploadDisabledInCurrentFolder
            );
          }
        },
      },
      {
        key: FileMenuActionEnum.CreateShortcut,
        icon: <Icon name="FileSymlinkIcon" className="h-5 w-5" />,
        label: translate('common.createShortcut'),
        condition: (context) => {
          return (
            context.files.length === 1 &&
            context.isAdminOrContributor &&
            !context.isInFileRoute(FilesTableEnum.Trash) &&
            !context.files.some((f) => f.isShortcut) &&
            !context.files.some((f) => isFileSpecialFolder(f)) &&
            !context.files.some((f) => f.isQuickShare)
          );
        },
      },
    ],
  },
  {
    key: FileMenuActionEnum.Details,
    label: translate('common.details'),
    icon: <Icon name="InfoIcon" className="h-5 w-5" />,
    condition: (context) => {
      return context.files.length === 1 && !context.isRightPanelOpen;
    },
  },
  {
    key: FileMenuActionEnum.Divider,
    type: 'divider',
  },
  {
    key: FileMenuActionEnum.Tag,
    label: translate('common.tag'),
    icon: <Icon name="TagIcon" className="mt-0.5 h-5 w-5" />,
    condition: (context) => {
      return (
        context.files.length === 1 &&
        context.tags.length > 0 &&
        !context.isInFileRoute(FilesTableEnum.Trash)
      );
    },
  },
  {
    key: FileMenuActionEnum.AddToFavorites,
    label: translate('common.addToFavorites'),
    icon: <Icon name="StarIcon" className="h-5 w-5" />,
    condition: (context) => {
      return (
        !context.files.some((f) => f.isFavourite) &&
        !context.isInFileRoute(FilesTableEnum.Trash) &&
        !context.files.some((f) => isFileSpecialFolder(f)) &&
        !context.files.some((f) => f.isQuickShare)
      );
    },
  },
  {
    key: FileMenuActionEnum.RemoveFromFavorites,
    label: translate('common.removeFromFavorites'),
    icon: <Icon name="StarOffIcon" className="h-5 w-5" />,
    condition: (context) => {
      return (
        context.files.some((f) => f.isFavourite) &&
        !context.isInFileRoute(FilesTableEnum.Trash) &&
        !context.files.some((f) => isFileSpecialFolder(f)) &&
        !context.files.some((f) => f.isQuickShare)
      );
    },
  },
  {
    key: FileMenuActionEnum.Hide,
    label: translate('common.hide'),
    icon: <Icon name="ArchiveRestoreIcon" className="h-5 w-5" />,
    condition: (context) => {
      return (
        !context.isInFileRoute(FilesTableEnum.Trash) &&
        !context.isInFileRoute(FilesTableEnum.Hidden)
      );
    },
  },
  {
    key: FileMenuActionEnum.Unhide,
    label: translate('common.unhide'),
    icon: <Icon name="ArchiveXIcon" className="h-5 w-5" />,
    condition: (context) => {
      return context.isInFileRoute(FilesTableEnum.Hidden);
    },
  },
  {
    key: FileMenuActionEnum.Delete,
    label: translate('common.delete'),
    icon: <Icon name="TrashIcon" className="h-5 w-5" />,
    danger: true,
    condition: (context) => {
      return (
        !context.isInFileRoute(FilesTableEnum.Received) &&
        !context.isInFileRoute(FilesTableEnum.Trash) &&
        context.isAdminOrContributor &&
        context.files.every(
          (f) => f?.ownerId === context.authenticatedUser?.userId
        ) &&
        !context.files.some((f) => isFileSpecialFolder(f))
      );
    },
  },
  {
    key: FileMenuActionEnum.RestoreFromTrash,
    label: translate('common.restoreFromTrash'),
    icon: <Icon name="FolderOutputIcon" className="h-5 w-5" />,
    condition: (context) => {
      return (
        context.isInFileRoute(FilesTableEnum.Trash) &&
        context.isAdminOrContributor &&
        context.files.every(
          (f) => f?.ownerId === context.authenticatedUser?.userId
        ) &&
        !context.files.some((f) => isFileSpecialFolder(f))
      );
    },
  },
  {
    key: FileMenuActionEnum.DeletePermanently,
    label: translate('common.deletePermanently'),
    icon: <Icon name="TrashIcon" className="h-5 w-5" />,
    danger: true,
    condition: (context) => {
      return (
        context.isInFileRoute(FilesTableEnum.Trash) &&
        context.isAdminOrContributor &&
        context.files.every(
          (f) => f?.ownerId === context.authenticatedUser?.userId
        ) &&
        !context.files.some((f) => isFileSpecialFolder(f))
      );
    },
  },
];

/**
 * Filters and processes a list of user actions recursively based on conditions and context.
 *
 * @param {FileAction[]} actions - The array of user actions to be filtered and processed recursively.
 * @param {FileActionContext} context - The context to evaluate the conditions of the actions.
 * @return {FileAction[]} A new array of user actions after filtering and recursively processing children actions.
 */
function filterActionsRecursive(
  actions: FileAction[],
  context: FileActionContext,
  withDividers: boolean = true,
  isTopbar: boolean = false
): FileAction[] {
  const filtered = actions
    .filter((a) => !a.condition || a.condition(context))
    .filter((a) => !(a.type === 'divider' && !withDividers))
    .filter((a) => a.onlyContextMenu != isTopbar)
    .map((a) => {
      const children = a.children
        ? filterActionsRecursive(a.children, context, withDividers, isTopbar)
        : undefined;
      return { ...a, children };
    })
    .filter((a) => {
      if (a.type === 'submenu') {
        return Array.isArray(a.children) && a.children.length > 0;
      }
      return true;
    });

  const cleaned: FileAction[] = [];
  for (const action of filtered) {
    if (action.type === 'divider') {
      const prev = cleaned[cleaned.length - 1];
      if (!prev || prev.type === 'divider') continue;
    }
    cleaned.push(action);
  }
  if (cleaned.length && cleaned[cleaned.length - 1].type === 'divider') {
    cleaned.pop();
  }

  return cleaned;
}

/**
 * Retrieves a list of filtered user actions based on the provided context.
 *
 * @param {FileActionContext} context - The context used to filter user actions.
 * @param {boolean} withDividers - Whether to include dividers in the filtered actions.
 * @param {boolean} isTopbar - Whether the actions are being filtered for a topbar context.
 * @return {FileAction[]} A list of user actions that match the filtering criteria.
 */
export function filteredFileActions(
  context: FileActionContext,
  withDividers: boolean = true,
  isTopbar: boolean = false
): FileAction[] {
  return filterActionsRecursive(fileActions, context, withDividers, isTopbar);
}
